import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { TextBlock } from '../../../Atoms/Text';
import { AdviseCurrentSpend } from '../../../../types/advise';
import { CxSpendHeader, CxSpendHeaderProps } from './CxSpendHeader';
import { SpendAmountInput } from './SpendAmountInput';
import { dollars } from './currentSupplierSpend';
import { overriddenLine, sourceLine } from './currentMonthlyCxSpend.calc';
import { useOverrideDraft } from './useOverrideDraft';

export interface SourcedCxSpendProps {
  spend: AdviseCurrentSpend;
  canEdit: boolean;
  onOverride: (monthlyAmount: number) => Promise<boolean>;
  onReset: () => Promise<boolean>;
}

/** The current spend Current Suppliers works out, with the Advisor's override of it. */
export const SourcedCxSpend = ({ spend, canEdit, onOverride, onReset }: SourcedCxSpendProps) => {
  const overrideDraft = useOverrideDraft(onOverride);
  const [resetting, setResetting] = useState(false);
  const { override, effective_monthly_amount: amount } = spend;
  const editing = overrideDraft.draft !== null;
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  const reset = async () => {
    setResetting(true);
    await onReset();
    setResetting(false);
  };

  const action: CxSpendHeaderProps['action'] =
    !canEdit || editing
      ? undefined
      : override
        ? { label: 'Reset', disabled: resetting, onClick: reset }
        : { label: 'Override', onClick: () => overrideDraft.start(amount) };

  return (
    <Box className="flex flex-col gap-1">
      <CxSpendHeader
        tag={override ? `Overridden · was ${dollars(override.sourced_monthly_amount)}` : 'From Current Suppliers'}
        action={action}
      />
      {editing ? (
        <SpendAmountInput
          text={overrideDraft.draft ?? ''}
          error={overrideDraft.error}
          inputRef={input}
          onChange={overrideDraft.change}
          onBlur={overrideDraft.save}
          onKeyDown={overrideDraft.onKeyDown}
        />
      ) : (
        <Box className="rounded border border-grey-3 bg-grey-1 px-3 py-2 text-right">
          <TextBlock
            size="sm14"
            weight="semibold"
            color="grey8">
            {dollars(amount)}
          </TextBlock>
        </Box>
      )}
      <TextBlock
        size="xs"
        color="grey6">
        {override ? overriddenLine(spend) : sourceLine(spend)}
      </TextBlock>
    </Box>
  );
};
